/**
 * Route Alert Service
 * Sends real-time monitoring alerts to the backend for notifications.
 *
 * This service is called by the route monitoring hooks when alerts are detected
 * (route deviations, delays, unplanned stops) to notify managers via push notifications.
 */

const API_BASE = '/api';

/**
 * Report a route deviation to the backend.
 * @param {Object} params - Deviation parameters
 * @param {number} params.routePlanId - Route plan ID
 * @param {number} params.vehicleId - Vehicle ID (route_plan_vehicles.id)
 * @param {number} params.distanceMeters - Distance from planned route in meters
 * @param {number} params.latitude - Current latitude
 * @param {number} params.longitude - Current longitude
 * @returns {Promise<Object>} Created event
 */
export const reportDeviation = async ({
  routePlanId,
  vehicleId,
  distanceMeters,
  latitude,
  longitude,
}) => {
  const response = await fetch(`${API_BASE}/route-plan-events/deviation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      routePlanId,
      vehicleId,
      distanceMeters,
      latitude,
      longitude,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to report deviation');
  }

  return response.json();
};

/**
 * Report a delay (late arrival prediction) to the backend.
 * @param {Object} params - Delay parameters
 * @param {number} params.routePlanId - Route plan ID
 * @param {number} params.vehicleId - Vehicle ID (optional)
 * @param {number} params.stopId - Stop ID
 * @param {number} params.delayMinutes - Predicted delay in minutes
 * @param {string} params.originalETA - Original ETA (ISO string)
 * @param {string} params.newETA - New predicted ETA (ISO string)
 * @returns {Promise<Object>} Created event
 */
export const reportDelay = async ({
  routePlanId,
  vehicleId,
  stopId,
  delayMinutes,
  originalETA,
  newETA,
}) => {
  const response = await fetch(`${API_BASE}/route-plan-events/delay`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      routePlanId,
      vehicleId,
      stopId,
      delayMinutes,
      originalETA,
      newETA,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to report delay');
  }

  return response.json();
};

/**
 * Report a generic monitoring alert to the backend.
 * @param {Object} params - Alert parameters
 * @param {number} params.routePlanId - Route plan ID
 * @param {number} params.vehicleId - Vehicle ID (optional)
 * @param {number} params.stopId - Stop ID (optional)
 * @param {string} params.eventType - Event type (route_deviation, late_arrival, etc.)
 * @param {string} params.severity - Severity (info, warning, error, critical)
 * @param {string} params.message - Alert message
 * @param {Object} params.extraData - Additional data
 * @returns {Promise<Object>} Created event
 */
export const reportAlert = async ({
  routePlanId,
  vehicleId,
  stopId,
  eventType,
  severity,
  message,
  extraData,
}) => {
  const response = await fetch(`${API_BASE}/route-plan-events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      routePlanId,
      vehicleId,
      stopId,
      eventType,
      severity,
      message,
      extraData,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to report alert');
  }

  return response.json();
};

/**
 * Get events for a route plan.
 * @param {number} routePlanId - Route plan ID
 * @param {Object} options - Query options
 * @param {string} options.severity - Filter by severity
 * @param {boolean} options.acknowledged - Filter by acknowledged status
 * @param {number} options.limit - Maximum number of events to return
 * @returns {Promise<Array>} List of events
 */
export const getEvents = async (routePlanId, options = {}) => {
  const params = new URLSearchParams({ routePlanId });

  if (options.severity) {
    params.append('severity', options.severity);
  }
  if (options.acknowledged !== undefined) {
    params.append('acknowledged', options.acknowledged);
  }
  if (options.limit) {
    params.append('limit', options.limit);
  }

  const response = await fetch(`${API_BASE}/route-plan-events?${params}`);

  if (!response.ok) {
    throw new Error('Failed to fetch events');
  }

  return response.json();
};

/**
 * Acknowledge an event.
 * @param {number} eventId - Event ID
 * @returns {Promise<Object>} Updated event
 */
export const acknowledgeEvent = async (eventId) => {
  const response = await fetch(`${API_BASE}/route-plan-events/${eventId}/acknowledge`, {
    method: 'PUT',
  });

  if (!response.ok) {
    throw new Error('Failed to acknowledge event');
  }

  return response.json();
};

/**
 * Acknowledge all events for a route plan.
 * @param {number} routePlanId - Route plan ID
 * @returns {Promise<Object>} Result with count of acknowledged events
 */
export const acknowledgeAllEvents = async (routePlanId) => {
  const response = await fetch(`${API_BASE}/route-plan-events/acknowledge-all?routePlanId=${routePlanId}`, {
    method: 'PUT',
  });

  if (!response.ok) {
    throw new Error('Failed to acknowledge events');
  }

  return response.json();
};

/**
 * Get count of unacknowledged events.
 * @param {number} routePlanId - Route plan ID (optional)
 * @returns {Promise<Object>} Count object with total, warnings, errors, criticals
 */
export const getUnacknowledgedCount = async (routePlanId) => {
  const params = routePlanId ? `?routePlanId=${routePlanId}` : '';
  const response = await fetch(`${API_BASE}/route-plan-events/unacknowledged-count${params}`);

  if (!response.ok) {
    throw new Error('Failed to fetch unacknowledged count');
  }

  return response.json();
};

// Re-export from routeMonitoringService (single source of truth)
export { ALERT_TYPES, ALERT_SEVERITY } from './routeMonitoringService';

export default {
  reportDeviation,
  reportDelay,
  reportAlert,
  getEvents,
  acknowledgeEvent,
  acknowledgeAllEvents,
  getUnacknowledgedCount,
};
